<template>
  <div id="app" role="main">
    <div class="container">
      <header>
        <h1>Apex箱子追踪器</h1>
        <div v-if="userId" class="user-id-display" aria-label="当前用户ID">
          用户ID: {{ userId }}
          <ActionButton 
            button-class="copy-btn" 
            @click="copyUserUrl" 
            aria-label="复制用户链接"
          >
            复制用户链接
          </ActionButton>
        </div>
      </header>
      
      <main>
        <ErrorBoundary>
          <!-- 总计统计 -->
          <StatsCard 
            title="总开启数量" 
            :count="formattedTotalOpened" 
            card-class="total-stats" 
            heading-id="total-stats-heading"
          />
          
          <!-- 今日统计 -->
          <StatsCard 
            title="今日开启" 
            :count="formattedTodayCount" 
            card-class="today-stats" 
            heading-id="today-stats-heading"
          >
            <!-- 今日开启按钮区域 -->
            <div class="today-controls">
              <div class="add-boxes">
                <ActionButton 
                  @click="() => incrementToday(1)" 
                  :disabled="loading.incrementToday" 
                  :loading="loading.incrementToday"
                  aria-label="增加1个今日开启数量"
                  button-class="quick-add-btn"
                >
                  +1
                </ActionButton>
                <ActionButton 
                  @click="() => incrementToday(5)" 
                  :disabled="loading.incrementToday" 
                  :loading="loading.incrementToday"
                  aria-label="增加5个今日开启数量"
                  button-class="quick-add-btn"
                >
                  +5
                </ActionButton>
                <ActionButton 
                  @click="() => incrementToday(10)" 
                  :disabled="loading.incrementToday" 
                  :loading="loading.incrementToday"
                  aria-label="增加10个今日开启数量"
                  button-class="quick-add-btn"
                >
                  +10
                </ActionButton>
                <ActionButton 
                  @click="() => incrementToday(100)" 
                  :disabled="loading.incrementToday" 
                  :loading="loading.incrementToday"
                  aria-label="增加100个今日开启数量"
                  button-class="quick-add-btn"
                >
                  +100
                </ActionButton>
              </div>
              
              <!-- 自定义数量输入区域 -->
              <div class="custom-input-section">
                <div class="custom-input-group">
                  <input 
                    type="number" 
                    v-model.number="customIncrementAmount" 
                    min="1"
                    class="custom-count-input"
                    placeholder="输入数量"
                    aria-label="自定义增加数量"
                  />
                  <ActionButton 
                    @click="() => incrementToday(customIncrementAmount || 0)" 
                    :disabled="loading.incrementToday || !customIncrementAmount || customIncrementAmount <= 0" 
                    :loading="loading.incrementToday"
                    aria-label="增加自定义数量"
                    button-class="custom-add-btn"
                  >
                    增加
                  </ActionButton>
                </div>
              </div>
            </div>
            
            <!-- 补录按钮 -->
            <div class="backfill-trigger">
              <ActionButton 
                button-class="backfill-btn" 
                @click="openBackfillModal" 
                aria-label="打开补录记录模态框"
              >
                补录记录
              </ActionButton>
            </div>
          </StatsCard>
          
          <!-- 每日记录 -->
          <DailyRecords 
            :records="dailyRecords" 
            @delete-record="confirmDeleteRecord"
          />
        </ErrorBoundary>
      </main>
    </div>
    
    <!-- 补录模态框 -->
    <BaseModal 
      :visible="showBackfillModal" 
      title="补录记录" 
      title-id="backfill-modal-title" 
      @close="closeBackfillModal"
    >
      <div class="backfill-form">
        <div class="form-group">
          <label for="backfill-date">选择日期:</label>
          <DatePicker 
            v-model="backfillDate" 
            placeholder="请选择日期"
            id="backfill-date"
            class="date-picker"
          />
        </div>
        <div class="form-group">
          <label for="backfill-count">开启数量:</label>
          <input 
            type="number" 
            id="backfill-count" 
            v-model.number="backfillCount" 
            min="0"
            class="count-input"
            aria-describedby="backfill-count-help"
          >
          <div id="backfill-count-help" class="form-help">请输入非负整数</div>
        </div>
        <div class="form-actions">
          <ActionButton 
            @click="submitBackfill" 
            :disabled="loading.submitBackfill" 
            :loading="loading.submitBackfill"
            button-class="submit-btn"
          >
            补录
          </ActionButton>
        </div>
      </div>
    </BaseModal>
    
    <!-- 用户ID设置模态框 -->
    <BaseModal 
      :visible="showUserIdModal" 
      title="设置用户ID" 
      title-id="userid-modal-title" 
      @close="closeUserIdModal"
    >
      <div class="user-id-form">
        <div class="form-group">
          <label for="user-id-input">请输入用户ID:</label>
          <input 
            type="text" 
            id="user-id-input" 
            v-model="newUserId" 
            placeholder="例如: player123"
            class="user-id-input"
            aria-describedby="user-id-help"
          >
          <div id="user-id-help" class="form-help">用户ID用于标识您的数据</div>
        </div>
        <ActionButton 
          @click="setUserId" 
          button-class="submit-btn"
        >
          确定
        </ActionButton>
        <div class="generate-id">
          <ActionButton 
            @click="generateRandomId" 
            button-class="generate-btn" 
            aria-label="生成随机用户ID"
          >
            生成随机ID
          </ActionButton>
        </div>
      </div>
    </BaseModal>
    
    <!-- 删除确认模态框 -->
    <BaseModal 
      :visible="showDeleteConfirmModal" 
      title="确认删除" 
      title-id="delete-modal-title" 
      @close="closeDeleteConfirmModal"
    >
      <div class="confirm-message">
        确定要删除 {{ formatDate(pendingDeleteRecord?.date) }} 的记录吗？
        <div class="record-preview">
          数量: {{ pendingDeleteRecord?.count }} 箱
        </div>
      </div>
      <div class="confirm-actions">
        <ActionButton 
          @click="closeDeleteConfirmModal" 
          button-class="cancel-btn" 
          aria-label="取消删除"
        >
          取消
        </ActionButton>
        <ActionButton 
          @click="deleteRecord" 
          :disabled="loading.deleteRecord" 
          :loading="loading.deleteRecord"
          button-class="confirm-delete-btn" 
          aria-label="确认删除"
        >
          确定删除
        </ActionButton>
      </div>
    </BaseModal>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue'
import DatePicker from './components/DatePicker.vue'
import StatsCard from './components/StatsCard.vue'
import DailyRecords from './components/DailyRecords.vue'
import BaseModal from './components/Modal.vue'
import ActionButton from './components/ActionButton.vue'
import ErrorBoundary from './components/ErrorBoundary.vue'
import { validateUserId, validateCount, validateBackfillData } from './utils/validators.js'
import { isOnline } from './utils/performance.js'
import layer from 'layui-layer'

export default {
  name: 'App',
  components: {
    DatePicker,
    StatsCard,
    DailyRecords,
    BaseModal,
    ActionButton,
    ErrorBoundary
  },
  setup() {
    // 数据状态
    const totalOpened = ref(0)
    const todayCount = ref(0)
    const dailyRecords = ref([])
    const backfillDate = ref('')
    const backfillCount = ref(0)
    const showBackfillModal = ref(false)
    const showUserIdModal = ref(false)
    const userId = ref('')
    const newUserId = ref('')
    const customIncrementAmount = ref(0) // 新增自定义数量输入
    // 删除确认模态框相关状态
    const showDeleteConfirmModal = ref(false)
    const pendingDeleteRecord = ref(null)
    // 加载状态
    const loading = ref({
      fetchData: false,
      incrementToday: false,
      submitBackfill: false,
      deleteRecord: false
    })
    
    // API基础URL - 使用代理路径
    const API_BASE_URL = '/tabs/apex'
    
    // 计算属性：今天日期
    const today = computed(() => {
      const now = new Date()
      return now.toISOString().split('T')[0]
    })
    
    // 格式化日期
    const formatDate = (dateString) => {
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }
    
    // 格式化大数字
    const formattedTotalOpened = computed(() => {
      return totalOpened.value;
    })
    
    const formattedTodayCount = computed(() => {
      return todayCount.value;
    })
    
    // 从URL获取用户ID
    const getUserIdFromUrl = () => {
      const urlParams = new URLSearchParams(window.location.search)
      return urlParams.get('user') || ''
    }
    
    // 设置用户ID到URL
    const setUserIdToUrl = (id) => {
      if (id) {
        const url = new URL(window.location)
        url.searchParams.set('user', id)
        window.history.replaceState({}, '', url)
      }
    }
    
    // 生成随机用户ID
    const generateRandomId = () => {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'
      let result = ''
      for (let i = 0; i < 8; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length))
      }
      newUserId.value = result
    }
    
    // 复制用户链接
    const copyUserUrl = () => {
      const url = window.location.href
      navigator.clipboard.writeText(url).then(() => {
        layer.msg('用户链接已复制到剪贴板！');
      })
    }
    
    // 打开用户ID设置模态框
    const openUserIdModal = () => {
      newUserId.value = userId.value
      showUserIdModal.value = true
    }
    
    // 关闭用户ID设置模态框
    const closeUserIdModal = () => {
      showUserIdModal.value = false
    }
    
    // 设置用户ID
    const setUserId = () => {
      if (validateUserId(newUserId.value)) {
        userId.value = newUserId.value.trim()
        setUserIdToUrl(userId.value)
        closeUserIdModal()
        // 创建用户并加载数据
        createUserIfNeeded().then(() => {
          fetchData()
        })
      } else {
        layer.msg('请输入有效的用户ID');
      }
    }
    
    // 检查用户是否存在
    const checkUserExists = async (userId) => {
      try {
        const response = await fetch(`${API_BASE_URL}/users/${userId}`)
        const result = await response.json()
        return result.data && result.data.exists || false
      } catch (error) {
        console.error('检查用户失败:', error)
        return false
      }
    }
    
    // 创建用户（如果不存在）
    const createUserIfNeeded = async () => {
      if (!userId.value) return
      
      try {
        // 先检查用户是否存在
        const exists = await checkUserExists(userId.value)
        if (!exists) {
          // 用户不存在，创建新用户
          const response = await fetch(`${API_BASE_URL}/users`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              userId: userId.value
            })
          })
          
          if (!response.ok) {
            const errorResult = await response.json()
            throw new Error(errorResult.message || '创建用户失败')
          }
        }
      } catch (error) {
        console.error('创建用户失败:', error)
        layer.msg('创建用户失败');
      }
    }
    
    // 获取用户每日记录
    const fetchDailyRecords = async () => {
      if (!userId.value) return;
      
      try {
        const recordsResponse = await fetch(`${API_BASE_URL}/users/${userId.value}/daily-records`)
        if (recordsResponse.ok) {
          const recordsResult = await recordsResponse.json()
          console.log('Records API response:', recordsResult) // 调试日志
          if (recordsResult.code === 0) {
            // 转换数据结构以匹配前端期望的格式，同时保留记录ID
            dailyRecords.value = (recordsResult.data || []).map(record => ({
              id: record.id, // 保留记录ID用于删除操作
              date: record.recordDate,
              count: record.count
            }))
          } else {
            throw new Error(recordsResult.message)
          }
        } else {
          throw new Error('获取每日记录失败')
        }
      } catch (error) {
        console.error('获取每日记录失败:', error)
        layer.msg('获取每日记录失败');
      }
    }
    
    // 从API获取数据（基于用户ID）
    const fetchData = async () => {
      if (!userId.value) {
        // 如果没有用户ID，显示空数据
        totalOpened.value = 0
        todayCount.value = 0
        dailyRecords.value = []
        return
      }
      
      try {
        // 获取用户统计数据
        const statsResponse = await fetch(`${API_BASE_URL}/users/${userId.value}/stats`)
        if (statsResponse.ok) {
          const statsResult = await statsResponse.json()
          console.log('Stats API response:', statsResult) // 调试日志
          if (statsResult.code === 0) {
            // 检查数据结构并正确提取totalOpened
            if (statsResult.data && statsResult.data.data) {
              totalOpened.value = statsResult.data.data.totalOpened || 0
            } else if (statsResult.data) {
              // 处理可能的扁平数据结构
              totalOpened.value = statsResult.data.totalOpened || 0
            } else {
              totalOpened.value = 0
            }
          } else {
            throw new Error(statsResult.message)
          }
        } else {
          throw new Error('获取统计数据失败')
        }
        
        // 获取用户每日记录
        await fetchDailyRecords()
        
        // 获取今日记录
        const todayResponse = await fetch(`${API_BASE_URL}/users/${userId.value}/daily-records/${today.value}`)
        if (todayResponse.ok) {
          const todayResult = await todayResponse.json()
          if (todayResult.code === 0 && todayResult.data) {
            todayCount.value = todayResult.data.count
          } else {
            todayCount.value = 0
          }
        } else {
          todayCount.value = 0
        }
      } catch (error) {
        console.error('获取数据失败:', error)
        layer.msg('获取数据失败');
        // 使用模拟数据作为后备
        totalOpened.value = 0
        todayCount.value = 0
        dailyRecords.value = [
          
        ]
      }
      
      // 设置默认补录日期为今天
      backfillDate.value = today.value
    }
    
    // 增加今日开启数量
    const incrementToday = async (amount) => {
      if (!userId.value) {
        openUserIdModal()
        return
      }
      
      // 验证数量
      if (!validateCount(amount)) {
        layer.msg('数量必须是非负整数');
        return;
      }
      
      try {
        // 设置加载状态
        loading.value.incrementToday = true
        
        const response = await fetch(`${API_BASE_URL}/users/${userId.value}/open`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            count: amount
          })
        })
        
        const result = await response.json()
        console.log('Open API response:', result) // 调试日志
        if (result.code === 0) {
          todayCount.value = result.data.data.todayCount
          totalOpened.value = result.data.data.totalOpened
          
          // 每次open调用后重新获取每日记录列表以确保数据同步
          await fetchDailyRecords()
        } else {
          throw new Error(result.message)
        }
      } catch (error) {
        console.error('增加今日开启数量失败:', error)
        layer.msg('增加今日开启数量失败');
      } finally {
        // 清除加载状态
        loading.value.incrementToday = false
      }
    }
    
    // 打开补录模态框
    const openBackfillModal = () => {
      if (!userId.value) {
        openUserIdModal()
        return
      }
      
      backfillDate.value = today.value
      backfillCount.value = 0
      showBackfillModal.value = true
    }
    
    // 关闭补录模态框
    const closeBackfillModal = () => {
      showBackfillModal.value = false
    }
    
    // 提交补录记录
    const submitBackfill = async () => {
      if (!userId.value) {
        openUserIdModal()
        return
      }
      
      // 验证补录数据
      const validation = validateBackfillData({
        date: backfillDate.value,
        count: backfillCount.value
      });
      
      if (!validation.isValid) {
        layer.msg(validation.errors.join(', '));
        return;
      }
      
      try {
        const response = await fetch(`${API_BASE_URL}/users/${userId.value}/backfill`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            date: backfillDate.value,
            count: backfillCount.value
          })
        })
        
        const result = await response.json()
        console.log('Backfill API response:', result) // 调试日志
        if (result.code === 0) {
          totalOpened.value = result.data.data.totalOpened
          
          // 补录记录后重新获取每日记录列表和今日记录以确保数据同步
          await fetchDailyRecords()
          
          // 如果补录的是今天的数据，也更新今日计数
          if (backfillDate.value === today.value) {
            const todayResponse = await fetch(`${API_BASE_URL}/users/${userId.value}/daily-records/${today.value}`);
            if (todayResponse.ok) {
              const todayResult = await todayResponse.json();
              if (todayResult.code === 0 && todayResult.data) {
                todayCount.value = todayResult.data.count;
              } else {
                todayCount.value = 0;
              }
            } else {
              todayCount.value = 0;
            }
          }
          
          // 关闭模态框并重置表单
          closeBackfillModal()
          console.log('API message:', result.message);
          console.log('Message type:', typeof result.message);
          // 显示API返回的第一级message
          const message = result.message || '补录成功！';
          console.log('Final message:', message);
          // 使用最简单的调用方式
          layer.msg(message);
        } else {
          throw new Error(result.message)
        }
      } catch (error) {
        console.error('补录记录失败:', error)
        layer.msg('补录失败');
      }
    }
    
    // 确认删除记录
    const confirmDeleteRecord = (record) => {
      pendingDeleteRecord.value = record
      showDeleteConfirmModal.value = true
    }
    
    // 关闭删除确认模态框
    const closeDeleteConfirmModal = () => {
      showDeleteConfirmModal.value = false
      pendingDeleteRecord.value = null
    }
    
    // 删除记录
    const deleteRecord = async () => {
      if (!userId.value || !pendingDeleteRecord.value) {
        layer.msg('缺少必要信息');
        return;
      }
      
      try {
        // 调用删除API - 根据接口文档调整URL和参数
        const response = await fetch(`${API_BASE_URL}/users/${userId.value}/deleteRecord?userId=${encodeURIComponent(userId.value)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json; charset=UTF-8'
          },
          body: JSON.stringify({
            id: pendingDeleteRecord.value.id
          })
        });
        
        const result = await response.json();
        if (result.code === 0) {
          // 删除成功，更新数据
          const deletedData = result.data.data;
          totalOpened.value = deletedData.totalOpened;
          
          // 删除记录后重新获取每日记录列表以确保数据同步
          await fetchDailyRecords()
          
          // 如果删除的是今天的记录，更新今日计数
          if (pendingDeleteRecord.value.date === today.value) {
            // 重新获取今日记录
            const todayResponse = await fetch(`${API_BASE_URL}/users/${userId.value}/daily-records/${today.value}`);
            if (todayResponse.ok) {
              const todayResult = await todayResponse.json();
              if (todayResult.code === 0 && todayResult.data) {
                todayCount.value = todayResult.data.count;
              } else {
                todayCount.value = 0;
              }
            } else {
              todayCount.value = 0;
            }
          }
          
          // 关闭模态框并提示成功
          closeDeleteConfirmModal();
          layer.msg(result.data.message || '记录删除成功');
        } else {
          throw new Error(result.message);
        }
      } catch (error) {
        console.error('删除记录失败:', error);
        layer.msg('删除记录失败');
      }
    }
    
    // 组件挂载时获取数据
    onMounted(async () => {
      // 检查网络状态
      if (!isOnline()) {
        layer.msg('您当前处于离线状态，部分功能可能受限');
      }
      
      // 从URL获取用户ID
      userId.value = getUserIdFromUrl()
      
      // 如果有用户ID，先检查用户是否存在
      if (userId.value) {
        try {
          const exists = await checkUserExists(userId.value)
          if (exists) {
            // 用户存在，直接加载数据
            fetchData()
          } else {
            // 用户不存在，提示用户创建
            layer.msg('用户不存在，请先创建用户');
            setTimeout(() => {
              openUserIdModal()
            }, 1000)
          }
        } catch (error) {
          console.error('检查用户失败:', error)
          layer.msg('检查用户失败');
        }
      } else {
        // 如果没有用户ID，提示用户设置
        setTimeout(() => {
          openUserIdModal()
        }, 1000)
      }
    })
    
// 防抖函数已移至utils/performance.js
    
    return {
      totalOpened,
      todayCount,
      dailyRecords,
      backfillDate,
      backfillCount,
      showBackfillModal,
      showUserIdModal,
      userId,
      newUserId,
      customIncrementAmount, // 自定义数量输入
      today,
      formatDate,
      formattedTotalOpened,
      formattedTodayCount,
      incrementToday,
      openBackfillModal,
      closeBackfillModal,
      submitBackfill,
      openUserIdModal,
      closeUserIdModal,
      setUserId,
      generateRandomId,
      copyUserUrl,
      // 删除功能相关
      showDeleteConfirmModal,
      pendingDeleteRecord,
      confirmDeleteRecord,
      closeDeleteConfirmModal,
      deleteRecord,
      // 获取每日记录
      fetchDailyRecords,
      // 加载状态
      loading
    }
  }
}
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: 'Arial', sans-serif;
  /* Apex主题渐变背景 - 深蓝到紫色 */
  background: linear-gradient(135deg, #0d1a2d 0%, #1a0d2d 50%, #2d0d1a 100%);
  min-height: 100vh;
  color: #fff;
}

#app {
  padding: 20px;
}

.container {
  max-width: 800px;
  margin: 0 auto;
}

header {
  text-align: center;
  margin-bottom: 30px;
}

header h1 {
  color: #ffcc00; /* Apex金色标题 */
  font-size: 2.5rem;
  text-shadow: 0 0 10px rgba(255, 204, 0, 0.5);
  letter-spacing: 1px;
  margin-bottom: 10px;
}

.user-id-display {
  color: #667eea;
  font-size: 1rem;
  margin-top: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
}

/* 补录触发器 */
.backfill-trigger {
  display: flex;
  justify-content: center;
  margin: 15px 0;

 
}

/* 今日控制区域 */
.today-controls {

    padding-bottom: 15px;
   border-bottom: 1px solid rgba(102, 126, 234, 0.3);
}

.add-boxes {
  display: flex;
  justify-content: center;
  gap: 15px;
  flex-wrap: wrap;
}

.quick-add-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 50px;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
  font-weight: bold;
  letter-spacing: 1px;
  min-width: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.quick-add-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.6);
}

.quick-add-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: 0 2px 10px rgba(102, 126, 234, 0.2);
}

/* 自定义输入区域 */
.custom-input-section {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid rgba(102, 126, 234, 0.3);
  display: flex;
  justify-content: center;
}

.custom-input-group {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
}

.custom-count-input {
  width: 120px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid rgba(102, 126, 234, 0.5);
  background: rgba(13, 26, 45, 0.7);
  color: white;
  font-size: 1rem;
  box-sizing: border-box;
  text-align: center;
}

.custom-count-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.5);
}

.custom-count-input::-webkit-outer-spin-button,
.custom-count-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.custom-count-input[type=number] {
  -moz-appearance: textfield;
}

.custom-add-btn {
  background: linear-gradient(135deg, #00c9a7 0%, #00a8ff 100%);
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 50px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 201, 167, 0.4);
  font-weight: bold;
  letter-spacing: 1px;
  min-width: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.custom-add-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #00a8ff 0%, #00c9a7 100%);
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0, 201, 167, 0.6);
}

.custom-add-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
  box-shadow: 0 2px 10px rgba(0, 201, 167, 0.2);
}

/* 补录触发器 */
.backfill-trigger {
  display: flex;
  justify-content: center;
  margin-bottom: 15px;
}

.backfill-btn {
  background: linear-gradient(135deg, #ffcc00 0%, #ff9900 100%);
  color: #0d1a2d;
  border: none;
  padding: 12px 25px;
  border-radius: 50px;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 204, 0, 0.4);
  font-weight: bold;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.backfill-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #ff9900 0%, #ffcc00 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 204, 0, 0.6);
}

/* 表单组 */
.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #ffcc00;
  font-weight: bold;
}

.count-input,
.user-id-input {
  width: 100%;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid rgba(102, 126, 234, 0.5);
  background: rgba(13, 26, 45, 0.7);
  color: white;
  font-size: 1rem;
  box-sizing: border-box;
}

.count-input:focus,
.user-id-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.5);
}

.form-help {
  margin-top: 5px;
  font-size: 0.85rem;
  color: #aaa;
}

/* 表单操作 */
.form-actions {
  display: flex;
  justify-content: center;
  margin-top: 10px;
}

/* 日期选择器 */
.date-picker {
  width: 100%;
}

/* 补录表单 */
.backfill-form {
  display: flex;
  flex-direction: column;
}

/* 用户ID表单 */
.user-id-form {
  display: flex;
  flex-direction: column;
}

.generate-id {
  margin-top: 15px;
  text-align: center;
}

/* 确认消息 */
.confirm-message {
  margin-bottom: 20px;
  text-align: center;
  color: #ffcc00;
}

.record-preview {
  margin-top: 10px;
  font-size: 1.1rem;
  color: #667eea;
  font-weight: bold;
}

.confirm-actions {
  display: flex;
  justify-content: center;
  gap: 15px;
  flex-wrap: wrap;
}

/* 移动端适配 */
@media (max-width: 768px) {
  #app {
    padding: 10px;
  }
  
  .container {
    padding: 0 5px;
  }
  
  header h1 {
    font-size: 2rem;
    margin-bottom: 15px;
  }
  
  .user-id-display {
    flex-direction: column;
    gap: 5px;
  }
  
  .add-boxes {
    gap: 10px;
  }
  
  .quick-add-btn {
    padding: 10px 15px;
    font-size: 1rem;
    min-width: 60px;
  }
  
  .custom-input-group {
    flex-direction: column;
    gap: 10px;
  }
  
  .custom-count-input {
    width: 100%;
  }
  
  .custom-add-btn {
    width: 100%;
  }
  
  .confirm-actions {
    gap: 10px;
  }
}

/* 小屏幕适配 */
@media (max-width: 480px) {
  header h1 {
    font-size: 1.8rem;
  }
  
  .add-boxes {
    gap: 8px;
  }
  
  .quick-add-btn {
    padding: 8px 12px;
    font-size: 0.9rem;
    min-width: 50px;
  }
  
  .form-group label {
    font-size: 0.9rem;
  }
  
  .count-input,
  .user-id-input {
    padding: 10px;
    font-size: 0.9rem;
  }
}
</style>